/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../lib/supabaseClient';

export interface NotificationTemplateRecord {
  id: string;
  name: string;
  channel: 'push' | 'sms' | 'email';
  title: string;
  body: string;
  dataSchema: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertNotificationTemplateInput {
  id?: string;
  name: string;
  channel: 'push' | 'sms' | 'email';
  title: string;
  body: string;
  dataSchema?: Record<string, unknown> | null;
  isActive?: boolean;
}

const CANDIDATE_TABLES = ['notification_templates', 'push_notification_templates'] as const;

let resolvedTable: string | null = null;
let resolvingPromise: Promise<string> | null = null;

async function resolveNotificationTable(): Promise<string> {
  if (resolvedTable) {
    return resolvedTable;
  }

  if (resolvingPromise) {
    return resolvingPromise;
  }

  resolvingPromise = (async () => {
    for (const table of CANDIDATE_TABLES) {
      const { error } = await supabase.from(table).select('id').limit(1);
      if (!error) {
        resolvedTable = table;
        return table;
      }
    }
    throw new Error(
      'Notification template table not found. Create a "notification_templates" table or update CANDIDATE_TABLES.'
    );
  })();

  return resolvingPromise;
}

const mapTemplate = (row: any): NotificationTemplateRecord => ({
  id: row.id,
  name: row.name,
  channel: (row.channel ?? 'push') as 'push' | 'sms' | 'email',
  title: row.title ?? '',
  body: row.body ?? '',
  dataSchema: typeof row.data_schema === 'object' ? (row.data_schema as Record<string, unknown>) : null,
  isActive: Boolean(row.is_active ?? true),
  createdAt: row.created_at ?? new Date().toISOString(),
  updatedAt: row.updated_at ?? new Date().toISOString(),
});

export async function listNotificationTemplates(): Promise<NotificationTemplateRecord[]> {
  const table = await resolveNotificationTable();
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map(mapTemplate);
}

export async function upsertNotificationTemplate(
  input: UpsertNotificationTemplateInput
): Promise<NotificationTemplateRecord> {
  const table = await resolveNotificationTable();
  const payload = {
    name: input.name,
    channel: input.channel,
    title: input.title,
    body: input.body,
    data_schema: input.dataSchema ?? null,
    is_active: input.isActive ?? true,
    updated_at: new Date().toISOString(),
  };

  const query = supabase
    .from(table)
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: 'id',
    })
    .select()
    .single();

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return mapTemplate(data);
}

export async function deleteNotificationTemplate(templateId: string) {
  const table = await resolveNotificationTable();
  const { error } = await supabase.from(table).delete().eq('id', templateId);
  if (error) {
    throw error;
  }
}

export interface PushBroadcastPayload {
  title: string;
  body: string;
  data?: Record<string, unknown>;
  userIds?: string[];
  tokens?: string[];
}

export interface PushBroadcastError {
  code?: string;
  message: string;
  token?: string;
}

export interface PushBroadcastResult {
  success: boolean;
  attempted?: number;
  delivered: number;
  failed?: number;
  invalidInputTokens?: number;
  invalidTokensRemoved?: number;
  pushErrors?: PushBroadcastError[];
}

export async function sendPushBroadcast(payload: PushBroadcastPayload): Promise<PushBroadcastResult> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session?.access_token) {
    throw new Error('You must be signed in to send push notifications.');
  }

  const { data, error } = await supabase.functions.invoke('send-push', {
    body: payload,
  });

  if (error) {
    throw new Error(error.message ?? 'Push delivery failed');
  }

  return (data as PushBroadcastResult | null) ?? { success: true, delivered: 0 };
}
